import { createMemo, createSignal } from "solid-js";
import Editor from "~/js/editor";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import type { Coordinates } from "~/solidJs/shared/helpers/types/ISizeble";
import { Menu } from "~/solidJs/shared/ui/molecules/Menu";

// Interface for unified state
interface NodeContextMenuState {
	isOpen: boolean;
	position: Coordinates;
	nodeUuid: string | null;
}

// Global signal for NodeContextMenu state
const [globalState, setGlobalState] = createSignal<NodeContextMenuState>({
	isOpen: false,
	position: { left: 0, top: 0 },
	nodeUuid: null
});

// Global function to open NodeContextMenu
export const openNodeContextMenu = (nodeUuid: string, position: Coordinates) => {
	const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
	if (node) {
		EventAnalytics.nodeContextMenu.openContextMenu(node.definition.globalId, {
			x: position.left,
			y: position.top
		});
	}

	setGlobalState({
		isOpen: true,
		position,
		nodeUuid
	});
};

// Global function to close NodeContextMenu
export const closeNodeContextMenu = () => {
	setGlobalState({
		isOpen: false,
		position: { left: 0, top: 0 },
		nodeUuid: null
	});
};

// Function to track node context menu actions
const trackNodeContextMenuAction = (menuItem: Menu.ClosedMenuItem) => {
	const nodeUuid = globalState().nodeUuid;
	if (nodeUuid) {
		const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
		if (node) {
			const globalId = node.definition.globalId;
			const action = menuItem.id;

			EventAnalytics.nodeContextMenu.contextMenuAction(action, globalId);
		}
	}
};

const createDeleteMenuItem = (closeMenu: () => void): Menu.ClosedMenuItem => {
	return {
		id: "delete-node",
		type: Menu.ClosedMenuItemType.Item,
		label: "Delete",
		onSelect(menuItem) {
			trackNodeContextMenuAction(menuItem);

			const nodeUuid = globalState().nodeUuid;
			if (nodeUuid) {
				const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
				if (node) {
					node.delete();
				}
			}
			closeMenu();
		}
	};
};

const createCloneMenuItem = (closeMenu: () => void): Menu.ClosedMenuItem => {
	return {
		id: "clone-node",
		type: Menu.ClosedMenuItemType.Item,
		label: "Clone",
		async onSelect(menuItem) {
			trackNodeContextMenuAction(menuItem);

			const nodeUuid = globalState().nodeUuid;
			if (nodeUuid) {
				const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
				if (node) {
					const copied = await Editor.context.graph.serializeNodesAndThierConnections(
						[node],
						false,
						true
					);
					// Offset the cloned node slightly to the right and down
					const offset = 50;
					for (const copiedNode of copied) {
						copiedNode.position.x += offset;
						copiedNode.position.y += offset;
					}
					Editor.context.graph.pasteNodes(copied);
				}
			}
			closeMenu();
		}
	};
};

const createCopyMenuItem = (closeMenu: () => void): Menu.ClosedMenuItem => {
	return {
		id: "copy-node",
		type: Menu.ClosedMenuItemType.Item,
		label: "Copy",
		async onSelect(menuItem) {
			trackNodeContextMenuAction(menuItem);

			const nodeUuid = globalState().nodeUuid;
			if (nodeUuid) {
				const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
				if (node) {
					EventAnalytics.nodeActions.copyNode();

					// First select the node, then copy
					Editor.context.graph.singleSelectNode(node);
					await Editor.context.graph.copySelectedNodes();
				}
			}
			closeMenu();
		}
	};
};

const _createSelectMenuItem = (closeMenu: () => void): Menu.ClosedMenuItem => {
	return {
		id: "select-node",
		type: Menu.ClosedMenuItemType.Item,
		label: "Select",
		onSelect(menuItem) {
			trackNodeContextMenuAction(menuItem);

			const nodeUuid = globalState().nodeUuid;
			if (nodeUuid) {
				const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
				if (node) {
					Editor.context.graph.singleSelectNode(node);
				}
			}
			closeMenu();
		}
	};
};

export const useNodeContextMenu = () => {
	const actions = createMemo((): Menu.ClosedMenuItem[] => {
		return [
			createCopyMenuItem(closeNodeContextMenu),
			createCloneMenuItem(closeNodeContextMenu),
			createDeleteMenuItem(closeNodeContextMenu)
		];
	});

	return {
		isOpen: () => globalState().isOpen,
		setIsOpen: (value: boolean) => {
			if (!value) {
				closeNodeContextMenu();
			} else {
				const currentState = globalState();
				setGlobalState({
					...currentState,
					isOpen: true
				});
			}
		},
		position: () => globalState().position,
		nodeUuid: () => globalState().nodeUuid,
		actions
	};
};
